import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram } from 'lucide-react';
import { siteInfo, heroImages } from '../../data/mock';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#FAF8F5]">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Soft gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAF8F5] via-[#FAF8F5] to-[#F5D5E3]/30" />
        
        {/* Floating decorative circles */}
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-[#E8A5C4]/10 blur-3xl animate-float" />
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-[#C45B9F]/10 blur-3xl animate-float delay-500" />
        
        {/* Decorative flower silhouette */}
        <div className="absolute right-0 top-1/4 w-1/3 h-2/3 opacity-5">
          <div className="w-full h-full bg-gradient-to-l from-[#8B4B7C] to-transparent rounded-l-full" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Location badges */}
            <div className="flex flex-wrap gap-2 animate-fadeIn">
              {siteInfo.locations.slice(0, 4).map((loc, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-white/80 text-[#6B5A62] shadow-sm border border-[#E8A5C4]/20"
                >
                  <span className="mr-1.5">{loc.flag}</span>
                  {loc.name}
                </span>
              ))}
            </div>

            {/* Main heading */}
            <div className="space-y-4">
              <div className="animate-fadeInUp">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tevaite-creations/artifacts/btmr9mu6_image.png"
                  alt="Tevaite Créations Florales"
                  className="h-36 md:h-44 lg:h-52 w-auto object-contain"
                />
              </div>
            </div>

            {/* Tagline */}
            <p className="text-lg md:text-xl text-[#8B4B7C]/80 font-heading italic max-w-lg animate-fadeInUp delay-300">
              "{siteInfo.tagline}"
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 animate-fadeInUp delay-400">
              <Link
                to="/creations"
                className="btn-tevaite inline-flex items-center px-8 py-4 rounded-full font-medium text-white"
              >
                Découvrir nos créations
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/contact"
                className="btn-tevaite-outline inline-flex items-center px-8 py-4 rounded-full font-medium"
              >
                Nous contacter
              </Link>
            </div>

            {/* Social link */}
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-[#C45B9F] hover:text-[#8B4B7C] transition-colors duration-300 animate-fadeIn delay-500"
            >
              <Instagram size={22} />
              <span className="font-medium">{siteInfo.instagram}</span>
            </a>
          </div>

          {/* Hero Image */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              {/* Main image */}
              <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl shadow-[#8B4B7C]/20">
                <img
                  src={heroImages[0]}
                  alt="Création florale tropicale"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#8B4B7C]/20 to-transparent" />
              </div>

              {/* Decorative frame */}
              <div className="absolute -top-6 -right-6 w-full h-full border-2 border-[#E8A5C4] rounded-[3rem] z-0" />
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl z-20 animate-bloom">
                <p className="font-script text-2xl text-[#8B4B7C]">Créations</p>
                <p className="text-sm text-[#6B5A62] mt-1">100% Tropicales</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#8B4B7C] rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-[#C45B9F] rounded-full mt-2 animate-pulse-soft" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
